import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Mic,
  Square,
  Upload,
  Download,
  Copy,
  RefreshCw,
  FileAudio,
  Type,
  Settings,
  Loader2
} from 'lucide-react';
import { speechToTextApi, audioUtilsApi, Language } from '../../services/audioApi';
import { AudioPageLayout, HistoryPanel, TransactionDrawer, HistoryItem } from '@/components/Audio';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';
import NextActionsPanel from '@/components/Content/NextActionsPanel';

const SpeechToText: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [language, setLanguage] = useState<string>('auto');
  const [provider, setProvider] = useState<'sarvam' | 'ai4bharat'>('sarvam');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active job polling state
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // Separate from activeJobId, which gets cleared back to null the moment a
  // job finishes (that's what stops polling) — this persists so the
  // next-actions panel below has something to show.
  const [completedJobId, setCompletedJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [jobCurrentStep, setJobCurrentStep] = useState<string>('');
  const [lastResult, setLastResult] = useState<{ confidence?: number; wordCount?: number; duration?: number } | null>(null);

  // History state
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeJobIdRef = useRef<string | null>(null);

  const isProcessing = jobStatus === 'processing' || jobStatus === 'pending';

  useEffect(() => {
    activeJobIdRef.current = activeJobId;
  }, [activeJobId]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await speechToTextApi.getJobs({ limit: 50 });
      const jobs = response?.data?.jobs || [];
      const items: HistoryItem[] = jobs.map((job: any) => ({
        id: job.jobId,
        title: job.title || `STT Job ${job.jobId.slice(0, 8)}`,
        createdAt: job.createdAt,
        status: job.status,
        type: 'speech-to-text',
        metadata: {
          language: job.request?.language,
          provider: job.request?.provider,
          duration: job.result?.duration ? `${job.result.duration.toFixed(1)}s` : undefined,
          wordCount: job.result?.wordCount,
          confidence: job.result?.confidence ? `${(job.result.confidence * 100).toFixed(0)}%` : undefined,
          transcription: job.result?.transcription,
          progress: (job.status === 'processing' || job.status === 'pending') ? `${job.progress}%` : undefined,
          currentStep: job.currentStep,
          error: job.error,
        },
      }));
      setHistoryItems(items);
    } catch (err) {
      console.error('Failed to fetch STT history:', err);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
    fetchHistory();
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  // Poll the active STT job until it completes or fails
  useEffect(() => {
    if (!activeJobId) return;

    const poll = async () => {
      const jobId = activeJobIdRef.current;
      if (!jobId) return;

      try {
        const response = await speechToTextApi.getSttJob(jobId);
        const data = response?.data;
        if (!data) return;

        setJobStatus(data.status);
        setJobProgress(data.progress ?? 0);
        setJobCurrentStep(data.currentStep || '');

        if (data.status === 'completed') {
          const text = data.result?.transcription || data.result?.transcript || data.transcription || '';
          setTranscription(text);
          setLastResult({
            confidence: data.result?.confidence,
            wordCount: data.result?.wordCount ?? data.result?.word_count,
            duration: data.result?.duration,
          });
          setCompletedJobId(jobId);
          setActiveJobId(null);
          fetchHistory();
        } else if (data.status === 'failed') {
          setError(data.error || 'Transcription failed');
          setActiveJobId(null);
          fetchHistory();
        }
      } catch (err) {
        console.error('Failed to poll STT job:', err);
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [activeJobId, fetchHistory]);

  const fetchLanguages = async () => {
    try {
      const response = await audioUtilsApi.getSupportedLanguages();
      setLanguages(response.data.languages);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
    }
  };

  const getSupportedMimeType = () => {
    const types = ['audio/webm', 'audio/mp4', 'audio/ogg'];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  };

  const getExtensionForMimeType = (mimeType: string) => {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('mp4')) return 'm4a';
    if (mimeType.includes('ogg')) return 'ogg';
    return 'webm';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        const extension = getExtensionForMimeType(actualMimeType);
        const audioFile = new File([audioBlob], `recording-${Date.now()}.${extension}`, {
          type: actualMimeType,
        });

        setAudioFile(audioFile);
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
        const newAudioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(newAudioUrl);

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setError('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const isAudioFile = (file: File): boolean => {
    if (file.type.startsWith('audio/')) return true;
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['mp3', 'wav', 'm4a', 'ogg', 'webm', 'flac', 'aac', 'mp4', 'mpeg', 'mpg', 'mpga', 'mp2'].includes(extension || '');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && isAudioFile(file)) {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      const newAudioUrl = URL.createObjectURL(file);
      setAudioFile(file);
      setAudioUrl(newAudioUrl);
      // Clear any previous run's output/error, but keep the file/URL just set above.
      setTranscription('');
      setLastResult(null);
      setError(null);
    } else {
      setError('Please select a valid audio file');
    }
  };

  const transcribeAudio = async () => {
    if (!audioFile) {
      setError('Please record or upload an audio file first');
      return;
    }

    setLoading(true);
    setError(null);
    setTranscription('');
    setLastResult(null);
    setActiveJobId(null);
    setCompletedJobId(null);
    setJobStatus('processing');
    setJobProgress(10);
    setJobCurrentStep('Uploading audio');

    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('provider', provider);
      if (title) {
        formData.append('title', title);
      }
      if (language !== 'auto') {
        formData.append('language', language);
      }

      const response = await speechToTextApi.transcribeAudio(formData);
      const jobId = response?.data?.jobId;
      if (!jobId) {
        throw new Error(response?.message || 'Failed to start transcription job');
      }

      setActiveJobId(jobId);
      setJobStatus(response.data.status || 'processing');
      setJobProgress(response.data.progress || 10);
      setJobCurrentStep(response.data.currentStep || 'Uploading audio');

      // Show the new job in history immediately
      fetchHistory();
    } catch (error: any) {
      console.error('Failed to transcribe audio:', error);
      setError(error.response?.data?.message || error.message || 'Failed to transcribe audio');
      setJobStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const copyTranscription = () => {
    navigator.clipboard.writeText(transcription);
  };

  const downloadTranscription = () => {
    const blob = new Blob([transcription], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcription.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCancelForm = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioFile(null);
    setAudioUrl(null);
    setTranscription('');
    setTitle('');
    setLanguage('auto');
    setProvider('sarvam');
    setLastResult(null);
    setError(null);
    setActiveJobId(null);
    setCompletedJobId(null);
    setJobStatus(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handlers
  const handleHistoryItemClick = (item: HistoryItem) => {
    setSelectedHistoryItem(item);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedHistoryItem(null), 300);
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      await speechToTextApi.retryJob(jobId);
      handleCloseDrawer();
      fetchHistory();
    } catch (err: any) {
      console.error('Failed to retry job:', err);
      setError(err.response?.data?.message || 'Failed to retry job');
    }
  };

  return (
    <>
      <AudioPageLayout
        title="Speech to Text"
        formSection={
          <div className="space-y-6">
            {/* Audio Input Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Audio Input</h2>

              {/* Recording Section */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Record Audio</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Mic className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  {isRecording ? (
                    <div className="space-y-4">
                      <div className="text-2xl font-mono text-red-600">
                        {formatTime(recordingTime)}
                      </div>
                      <button
                        onClick={stopRecording}
                        className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Square className="w-5 h-5 mr-2" />
                        Stop Recording
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={startRecording}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      Start Recording
                    </button>
                  )}
                </div>
              </div>

              {/* File Upload Section */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Audio File</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileAudio className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 mb-4">
                    Support: MP3, WAV, M4A, OGG, MPEG (Max 25MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,.mpeg,.mpg,.mpga,.mp2"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose File
                  </button>
                </div>
              </div>

              {/* Audio File Display */}
              {audioFile && (
                <div className="mb-6 p-4 border border-gray-300 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileAudio className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {audioFile.name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatFileSize(audioFile.size)}
                      </p>
                    </div>
                  </div>
                  {audioUrl && (
                    <audio
                      src={audioUrl}
                      controls
                      className="w-full"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </div>
              )}

              {/* Title Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transcription Title <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Meeting Notes - April 11"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={200}
                />
              </div>

              {/* Provider Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  STT Provider
                </label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as 'sarvam' | 'ai4bharat')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="sarvam">Sarvam (default, auto-detect supported)</option>
                  <option value="ai4bharat">AI4Bharat IndicConformer</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {provider === 'ai4bharat'
                    ? 'IndicConformer — needs an explicit language, defaults to Hindi if left on Auto-detect'
                    : 'Sarvam — cloud API, supports auto language detection'}
                </p>
              </div>

              {/* Language Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="auto">Auto-detect</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name} ({lang.nativeName})
                    </option>
                  ))}
                </select>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Processing Progress */}
              {isProcessing && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm font-medium text-blue-800">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {jobCurrentStep || 'Transcribing...'}
                    </div>
                    <span className="text-sm font-medium text-blue-800">{jobProgress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${jobProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Transcribe Button */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelForm}
                  disabled={loading || isProcessing}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={transcribeAudio}
                  disabled={loading || isProcessing || !audioFile}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Transcribing...
                    </>
                  ) : loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Type className="w-5 h-5 mr-2" />
                      Transcribe Audio
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Transcription Output Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Transcription</h2>
                {transcription && (
                  <div className="flex space-x-2">
                    <button
                      onClick={copyTranscription}
                      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={downloadTranscription}
                      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Download as text file"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>

              {transcription ? (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <LocalizedTextArea
                      language={language !== 'auto' ? language : undefined}
                      value={transcription}
                      onChange={(e) => setTranscription(e.target.value)}
                      className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Transcription will appear here..."
                    />
                  </div>
                  <div className="text-sm text-gray-600 flex flex-wrap gap-x-4">
                    <span>Words: {transcription.split(/\s+/).filter(word => word.length > 0).length}</span>
                    <span>Characters: {transcription.length}</span>
                    {lastResult?.duration !== undefined && <span>Audio duration: {lastResult.duration.toFixed(1)}s</span>}
                    {lastResult?.confidence !== undefined && <span>Confidence: {(lastResult.confidence * 100).toFixed(0)}%</span>}
                  </div>
                </div>
              ) : isProcessing ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">{jobCurrentStep || 'Transcribing...'}</p>
                  <p className="text-sm text-gray-500">This can take a moment for longer recordings</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Type className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No transcription yet</p>
                  <p className="text-sm text-gray-500">
                    Record or upload audio to get started
                  </p>
                </div>
              )}
            </div>

            {completedJobId && (
              <NextActionsPanel contentType="stt_job" contentId={completedJobId} />
            )}
          </div>
        }
        historySection={
          <HistoryPanel
            items={historyItems}
            onItemClick={handleHistoryItemClick}
            selectedItemId={selectedHistoryItem?.id}
          />
        }
      />
      
      {/* Transaction Details Drawer */}
      <TransactionDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        item={selectedHistoryItem}
        renderDetails={(item) => {
          const drawerTranscription: string | undefined = item.metadata?.transcription;
          const drawerDownload = () => {
            if (!drawerTranscription) return;
            const blob = new Blob([drawerTranscription], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${item.title || 'transcription'}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          };

          return (
            <>
              <div className="grid grid-cols-2 gap-4">
                {item.metadata?.language && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Language</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.language}</p>
                  </div>
                )}
                {item.metadata?.provider && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Provider</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 capitalize">{item.metadata.provider}</p>
                  </div>
                )}
                {item.metadata?.duration && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.duration}</p>
                  </div>
                )}
                {item.metadata?.wordCount !== undefined && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Word Count</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.wordCount}</p>
                  </div>
                )}
                {item.metadata?.confidence && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Confidence</label>
                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{item.metadata.confidence}</p>
                  </div>
                )}
              </div>

              {drawerTranscription && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Transcription</label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => navigator.clipboard.writeText(drawerTranscription)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={drawerDownload}
                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Download as text file"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 max-h-64 overflow-y-auto">
                    {drawerTranscription}
                  </p>
                </div>
              )}

              {item.status === 'failed' && item.metadata?.error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{item.metadata.error}</p>
                </div>
              )}

              {item.status === 'failed' && (
                <button
                  onClick={() => handleRetryJob(item.id)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-sm font-medium rounded-lg border border-indigo-200 dark:border-indigo-800 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Retry Job
                </button>
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default SpeechToText;
